import { zodResolver } from "@hookform/resolvers/zod";
import { Button, Card, Loader, Stack, Title } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { createFileRoute, Outlet, useLocation } from "@tanstack/react-router";
import dayjs from "dayjs";
import { useEffect } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { FaTruckFront } from "react-icons/fa6";
import { toast } from "react-toastify";

import { Input } from "@/components/@custom/Input";
import { DateRangeQueryParamsArgs, dateRangeQueryParamsSchema, useDriverLogsQueryParams } from "@/features/app/logs/hooks";
import { useSession } from "@/hooks/@custom/useSession";
import { DATE_PATTERNS } from "@/lib/Mantine";
import { useListLogDriverInfiniteQuery } from "@/store/apis/nextcargo/log-driver";
import { logDriverApi } from "@/store/apis/nextcargo/log-driver/logDriver.api";
import { schedulingApi } from "@/store/apis/nextcargo/scheduling/scheduling.api";
import { cn } from "@/utils/cn";

export const Route = createFileRoute("/app/logs")({
  component: RouteComponent,
  validateSearch: dateRangeQueryParamsSchema,
  staleTime: 0,
  context: ({ context }) => {
    const store = context.store;
    const state = store.getState();

    const dispatch = store.dispatch;
    const driverId = state.auth.driver?.id;

    async function getLogs({ dateRange = ["", ""] }: DateRangeQueryParamsArgs) {
      if (!driverId) return [];
      const [startDate, endDate] = dateRange;

      try {
        const logs = await dispatch(
          logDriverApi.endpoints.listLogDriver.initiate(
            {
              id: driverId,
              startDate,
              endDate
            },
            { subscribe: true }
          )
        ).unwrap();

        if (!toast.isActive("logs-success")) {
          toast.success("Consulta de Registros finalizada!", { toastId: "logs-success" });
        }

        return logs.pages.flatMap((item) => item.data);
      } catch (error) {
        console.error(error);
        toast.error("Erro ao consultar!");
        return [];
      }
    }

    async function getHistory({ dateRange = ["", ""] }: DateRangeQueryParamsArgs) {
      if (!driverId) return [];
      const [minDate, maxDate] = dateRange;

      try {
        const data = await dispatch(
          schedulingApi.endpoints.listSchedulingDeletedByDriver.initiate({
            id: driverId,
            minDate,
            maxDate
          })
        ).unwrap();

        const filteredData = data.pages.flatMap((page) => page.data).filter((item) => item?.reason === "TEMPO EXPIRADO");

        if (!toast.isActive("history-success")) {
          toast.success("Consulta de Histórico finalizada!", { toastId: "history-success" });
        }

        return filteredData;
      } catch (err) {
        console.log(err);
        toast.error("Erro ao consultar!");
        return [];
      }
    }

    return {
      getLogs,
      getHistory
    };
  }
});

function RouteComponent() {
  const [query, setQuery] = useDriverLogsQueryParams();
  const { driver } = useSession();
  const { getLogs, getHistory } = Route.useRouteContext();
  const { pathname } = useLocation();

  const isMobile = useMediaQuery("(max-width: 768px)");
  const isExpiredsPage = pathname === "/app/logs/expirados";
  const queueHeaderTitle = isExpiredsPage ? "Autorizações Expiradas" : "Registros do motorista";
  const DATE_PATTERN = DATE_PATTERNS.GLOBAL.DATE;

  const today = dayjs();
  const aWeekAgo = today.subtract(7, "days");
  const todayFormatted = today.format(DATE_PATTERN);
  const maxDate = today.format(DATE_PATTERN);

  const defaultRange = query?.dateRange || [aWeekAgo.format(DATE_PATTERN), todayFormatted];

  const { isFetching, fetchNextPage, hasNextPage, isFetchingNextPage } = useListLogDriverInfiniteQuery(
    {
      id: Number(driver?.id),
      startDate: query?.dateRange?.[0],
      endDate: query?.dateRange?.[1]
    },
    { skip: !isExpiredsPage }
  );

  const {
    formState: { isSubmitting, isValid },
    handleSubmit,
    control,
    reset
  } = useForm({
    mode: "all",
    disabled: isFetching,
    resolver: zodResolver(dateRangeQueryParamsSchema),
    defaultValues: {
      dateRange: defaultRange
    }
  });
  const onSubmit: SubmitHandler<DateRangeQueryParamsArgs> = async (data) => {
    const { dateRange } = data;
    setQuery({ dateRange });

    if (!isExpiredsPage) {
      await getLogs({ dateRange });
    } else {
      await getHistory({ dateRange });
    }
  };

  useEffect(() => {
    reset();
  }, [pathname]);

  /* useEffect(() => {
    const { dateRange: range } = getValues();
    const dateRange = range as [string, string];

    if (!isExpiredsPage) {
      getLogs({ dateRange });
    } else {
      getHistory({ dateRange });
    }
  }, [isExpiredsPage]); */

  return (
    <div className="mt-6 mb-4 w-full max-w-2xl self-start!">
      <Card shadow="md" radius="md" withBorder>
        <Stack>
          <div className="flex items-center gap-2 rounded-md bg-gray-100 px-4 py-2 text-gray-700">
            <FaTruckFront size={28} />
            <Title order={1} fw="bold" className="md:text-lg!">
              {queueHeaderTitle}
            </Title>
          </div>

          <form onSubmit={handleSubmit(onSubmit)}>
            <Stack>
              <Input.DateRange control={control} name="dateRange" numberOfColumns={isMobile ? 1 : 2} maxDate={maxDate} className="self-center" allowSingleDateInRange />

              <div className={cn("grid grid-cols-1 gap-1", { "md:grid-cols-2": !isExpiredsPage })}>
                <Button type="submit" color="blue" size="md" loading={isSubmitting || (isFetching && !isFetchingNextPage)} disabled={!isValid} fullWidth>
                  Aplicar
                </Button>

                {!isExpiredsPage && (
                  <Button onClick={fetchNextPage} color="blue" size="md" loading={isFetchingNextPage} disabled={!hasNextPage} fullWidth>
                    Buscar Mais
                  </Button>
                )}
              </div>
            </Stack>
          </form>
        </Stack>
      </Card>

      <div className="mt-2 min-h-[200px] rounded has-[.empty]:flex has-[.empty]:items-center has-[.empty]:justify-center has-[.empty]:bg-gray-50 has-[.empty]:p-4">
        {isFetching && (
          <Stack align="center" justify="center">
            <Loader className="mx-auto!" />
          </Stack>
        )}
        {!isFetching && <Outlet />}
      </div>
    </div>
  );
}
